import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { createPortal } from "react-dom";
import {
  Dialog,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FiKey, FiMail, FiShield, FiSmartphone, FiTrash2 } from "react-icons/fi";
import { RiWhatsappLine } from "react-icons/ri";
import { TbShieldLock } from "react-icons/tb";
import { MdClose } from "react-icons/md";
import { useSnackbar } from "notistack";
import QRCode from "react-qr-code";
import { UserContext } from "../../../Contexts/User";
import { FidoTouchPanel } from "../FidoTouchPanel";
import {
  MAX_SELECTED_LOGIN_METHODS,
  buildGoogleAuthenticatorOtpauthUrl,
} from "../../../Constants/webauthnMock";
import { fetchAuthenticatorSecret } from "../../../Services/twoFactorAuthenticatorApi";
import {
  getWebAuthnUnavailableMessage,
  isWebAuthnAvailable,
} from "../../../Services/webauthnClient";

const MODAL_PAPER_MOBILE =
  "!flex !flex-col !h-full !overflow-x-hidden !shadow-card app-card";
const MODAL_PAPER_DESKTOP =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-full !max-w-[calc(100vw-2rem)] !overflow-x-hidden sm:!max-w-3xl";

const METHOD_CONFIG = [
  {
    id: "email",
    titleKey: "words.email",
    segmentKey: "words.email",
    descriptionKey: "twoFactor.emailDescription",
    icon: FiMail,
  },
  {
    id: "sms",
    titleKey: "words.sms",
    segmentKey: "words.sms",
    descriptionKey: "twoFactor.smsDescription",
    icon: FiSmartphone,
  },
  {
    id: "whatsapp",
    titleKey: "words.whatsapp",
    segmentKey: "words.whatsapp",
    descriptionKey: "twoFactor.whatsappDescription",
    icon: RiWhatsappLine,
  },
  {
    id: "google_authenticator",
    titleKey: "words.googleAuthenticator",
    segmentKey: "words.google",
    descriptionKey: "twoFactor.googleDescription",
    icon: TbShieldLock,
  },
  {
    id: "fido",
    titleKey: "words.usbKey",
    segmentKey: "words.usbKey",
    descriptionKey: "twoFactor.fidoDescription",
    icon: FiKey,
  },
];

const SEGMENT_METHOD_IDS = [
  "email",
  "sms",
  "whatsapp",
  "google_authenticator",
  "fido",
];

const buildMethod = (config) => ({
  ...config,
  title: intl.get(config.titleKey),
  segmentLabel: intl.get(config.segmentKey),
  description: intl.get(config.descriptionKey),
});

const LOGIN_METHOD_OPTIONS = [
  { id: "email", labelKey: "words.email" },
  { id: "sms", labelKey: "words.sms" },
  { id: "whatsapp", labelKey: "words.whatsapp" },
  { id: "google_authenticator", labelKey: "words.googleAuthenticator" },
  { id: "fido", labelKey: "words.usbKey" },
];

const isMethodAllowedByPolicy = (methodId, allowed) => {
  if (methodId === "whatsapp") return allowed.includes("whatsapp");
  return allowed.includes(methodId);
};

const formatDate = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale || "pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const MethodPanelHeader = ({ method, statusLabel, statusClass }) => {
  const Icon = method.icon;

  return (
    <div className="mb-4 flex items-start gap-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-[#e7a73f] shadow-sm dark:bg-gray-800">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {method.title}
          </p>
          <span className={`text-xs font-medium ${statusClass}`}>{statusLabel}</span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {method.description}
        </p>
      </div>
    </div>
  );
};

export const TwoFactorSettingsModal = ({
  open,
  onClose,
  settings,
  registerFidoKey,
  accountKey,
  removeFidoKey,
  setPreferredLogin,
  setSelectedLoginMethods,
  setPersonPhone,
  configureGoogleAuthenticator,
  removeGoogleAuthenticator,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const [stateUserContext] = useContext(UserContext);
  const userLocale = stateUserContext.userLanguage || "pt-BR";
  const [activeMethodId, setActiveMethodId] = useState("email");
  const [registerOverlayOpen, setRegisterOverlayOpen] = useState(false);
  const [keyLabel, setKeyLabel] = useState("");
  const [webAuthnInProgress, setWebAuthnInProgress] = useState(false);
  const [googleSecret, setGoogleSecret] = useState("");
  const [googleOtpauthUrl, setGoogleOtpauthUrl] = useState("");
  const [googleSecretLoading, setGoogleSecretLoading] = useState(false);
  const [googleSecretError, setGoogleSecretError] = useState("");
  const registerOverlayActiveRef = useRef(false);
  const registerAttemptRef = useRef(0);

  const hasFidoKey = settings.credentials.length > 0;
  const webAuthnAvailable = isWebAuthnAvailable();
  const googleAuthenticatorConfigured =
    settings.googleAuthenticatorConfigured === true;
  const googleAuthAllowed = settings.policy.allowed.includes(
    "google_authenticator"
  );
  const isRegistering = registerOverlayOpen || webAuthnInProgress;
  const selectedLoginMethods = settings.selectedLoginMethods || [];
  const preferredLogin = settings.preferredLogin || selectedLoginMethods[0] || "email";
  const atLoginMethodLimit =
    selectedLoginMethods.length >= MAX_SELECTED_LOGIN_METHODS;
  const hasGoogleSecret = Boolean(googleSecret);

  const visibleMethods = useMemo(
    () =>
      SEGMENT_METHOD_IDS.map((id) => METHOD_CONFIG.find((method) => method.id === id))
        .filter(
          (method) =>
            method &&
            isMethodAllowedByPolicy(method.id, settings.policy.allowed)
        )
        .map(buildMethod),
    [settings.policy.allowed]
  );

  const activeMethod =
    visibleMethods.find((method) => method.id === activeMethodId) ||
    visibleMethods[0];

  useEffect(() => {
    if (!open || visibleMethods.length === 0) return;
    setActiveMethodId((current) => {
      const stillVisible = visibleMethods.some((method) => method.id === current);
      return stillVisible ? current : visibleMethods[0].id;
    });
  }, [open, visibleMethods]);

  useEffect(() => {
    if (!open || !googleAuthAllowed) return;

    // Secret/QR are only for initial setup — clear once configured.
    if (googleAuthenticatorConfigured) {
      setGoogleSecret("");
      setGoogleOtpauthUrl("");
      setGoogleSecretError("");
      setGoogleSecretLoading(false);
      return;
    }

    let cancelled = false;
    setGoogleSecretLoading(true);
    setGoogleSecretError("");

    fetchAuthenticatorSecret()
      .then((result) => {
        if (cancelled) return;
        const secret = result.secret;
        const otpauth =
          result.otpauthUrl ||
          buildGoogleAuthenticatorOtpauthUrl(
            secret,
            result.account || accountKey || "conta"
          );
        setGoogleSecret(secret);
        setGoogleOtpauthUrl(otpauth);
        if (!secret) {
          setGoogleSecretError(intl.get("twoFactor.googleSecretUnavailable"));
        }
      })
      .catch(() => {
        if (cancelled) return;
        setGoogleSecret("");
        setGoogleOtpauthUrl("");
        setGoogleSecretError(intl.get("twoFactor.googleSecretLoadError"));
      })
      .finally(() => {
        if (!cancelled) setGoogleSecretLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountKey, googleAuthAllowed, googleAuthenticatorConfigured, open]);

  const handlePreferredLoginChange = useCallback(
    (methodId) => {
      if (!setPreferredLogin) return;

      const nextSelected = selectedLoginMethods.includes(methodId)
        ? selectedLoginMethods
        : atLoginMethodLimit
          ? selectedLoginMethods
          : [...selectedLoginMethods, methodId];

      if (!nextSelected.includes(methodId)) return;

      if (nextSelected !== selectedLoginMethods) {
        setSelectedLoginMethods(nextSelected);
      }
      setPreferredLogin(methodId);
    },
    [
      atLoginMethodLimit,
      selectedLoginMethods,
      setPreferredLogin,
      setSelectedLoginMethods,
    ]
  );

  const handleBackupMethodToggle = useCallback(
    (methodId) => {
      if (methodId === preferredLogin) return;

      const isSelected = selectedLoginMethods.includes(methodId);
      if (isSelected) {
        setSelectedLoginMethods(
          selectedLoginMethods.filter((item) => item !== methodId)
        );
        return;
      }
      if (atLoginMethodLimit) return;
      setSelectedLoginMethods([...selectedLoginMethods, methodId]);
    },
    [
      atLoginMethodLimit,
      preferredLogin,
      selectedLoginMethods,
      setSelectedLoginMethods,
    ]
  );

  const resetRegisterFlow = useCallback(() => {
    registerOverlayActiveRef.current = false;
    registerAttemptRef.current = 0;
    setRegisterOverlayOpen(false);
    setKeyLabel("");
  }, []);

  const handleClose = useCallback(() => {
    if (webAuthnInProgress) return;
    resetRegisterFlow();
    onClose?.();
  }, [onClose, resetRegisterFlow, webAuthnInProgress]);

  const handleDialogClose = useCallback(
    (_event, reason) => {
      if (
        webAuthnInProgress ||
        registerOverlayActiveRef.current ||
        registerOverlayOpen
      ) {
        return;
      }
      if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      handleClose();
    },
    [handleClose, registerOverlayOpen, webAuthnInProgress]
  );

  const handleOpenRegisterOverlay = useCallback((event) => {
    event?.stopPropagation();
    registerAttemptRef.current = 0;
    registerOverlayActiveRef.current = true;
    setRegisterOverlayOpen(true);
  }, []);

  const handleCloseRegisterOverlay = useCallback(() => {
    if (webAuthnInProgress) return;
    registerOverlayActiveRef.current = false;
    setRegisterOverlayOpen(false);
  }, [webAuthnInProgress]);

  const handleRegisterSuccess = useCallback(() => {
    enqueueSnackbar(intl.get("twoFactor.keyRegisteredSuccess"), {
      variant: "success",
    });
    resetRegisterFlow();
  }, [enqueueSnackbar, resetRegisterFlow]);

  const handleRegisterError = useCallback((message) => {
    enqueueSnackbar(message, { variant: "warning" });
  }, [enqueueSnackbar]);

  const handleRegisterWebAuthn = useCallback(async () => {
    registerAttemptRef.current += 1;
    await registerFidoKey(keyLabel, {
      userId: accountKey,
      username: accountKey,
      registrationAttempt: registerAttemptRef.current,
    });
  }, [accountKey, keyLabel, registerFidoKey]);

  const handleRemoveKey = useCallback(
    async (credentialId) => {
      try {
        await removeFidoKey(credentialId);
        enqueueSnackbar(intl.get("twoFactor.keyRemoved"), { variant: "info" });
      } catch {
        enqueueSnackbar(intl.get("twoFactor.keyRemoveError"), { variant: "error" });
      }
    },
    [enqueueSnackbar, removeFidoKey]
  );

  const getMethodStatus = (methodId) => {
    if (methodId === "email") {
      return selectedLoginMethods.includes("email")
        ? intl.get("status.active")
        : intl.get("status.available");
    }
    if (methodId === "sms") {
      return selectedLoginMethods.includes("sms")
        ? intl.get("status.active")
        : intl.get("status.available");
    }
    if (methodId === "whatsapp") {
      return selectedLoginMethods.includes("whatsapp")
        ? intl.get("status.active")
        : intl.get("status.available");
    }
    if (methodId === "google_authenticator") {
      if (!googleAuthenticatorConfigured) return intl.get("status.setup");
      return selectedLoginMethods.includes("google_authenticator")
        ? intl.get("status.active")
        : intl.get("status.configured");
    }
    if (methodId === "fido") {
      return hasFidoKey ? intl.get("status.registered") : intl.get("status.register");
    }
    return intl.get("status.comingSoon");
  };

  const getStatusClass = (methodId) => {
    if (methodId === "email" && selectedLoginMethods.includes("email")) {
      return "text-emerald-600";
    }
    if (methodId === "sms" && selectedLoginMethods.includes("sms")) {
      return "text-emerald-600";
    }
    if (methodId === "whatsapp" && selectedLoginMethods.includes("whatsapp")) {
      return "text-emerald-600";
    }
    if (
      methodId === "google_authenticator" &&
      googleAuthenticatorConfigured
    ) {
      return "text-emerald-600";
    }
    if (methodId === "google_authenticator") return "text-[#e7a73f]";
    if (methodId === "fido" && hasFidoKey) return "text-emerald-600";
    if (methodId === "fido") return "text-[#e7a73f]";
    return "text-gray-400";
  };

  const handleConfigureGoogleAuthenticator = useCallback(() => {
    if (!hasGoogleSecret) {
      enqueueSnackbar(intl.get("twoFactor.googleSecretUnavailable"), {
        variant: "warning",
      });
      return;
    }
    configureGoogleAuthenticator?.();
    enqueueSnackbar(intl.get("twoFactor.googleConfiguredSuccess"), {
      variant: "success",
    });
  }, [configureGoogleAuthenticator, enqueueSnackbar, hasGoogleSecret]);

  const handleRemoveGoogleAuthenticator = useCallback(() => {
    removeGoogleAuthenticator?.();
    enqueueSnackbar(intl.get("twoFactor.googleRemoved"), { variant: "info" });
  }, [enqueueSnackbar, removeGoogleAuthenticator]);

  const renderOtpMethodPanel = (method) => (
    <div role="tabpanel" aria-label={method.title}>
      <MethodPanelHeader
        method={method}
        statusLabel={getMethodStatus(method.id)}
        statusClass={getStatusClass(method.id)}
      />
      <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-4 dark:border-gray-800 dark:bg-gray-900/40">
        <p className="text-sm text-brand-dark dark:text-gray-100">
          {intl.get("twoFactor.methodAvailable")}
        </p>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {method.id === "email"
            ? intl.get("twoFactor.otpAutoSendEmail")
            : method.id === "sms"
              ? intl.get("twoFactor.otpAutoSendSms")
              : intl.get("twoFactor.otpAutoSendWhatsapp")}{" "}
          {intl.get("twoFactor.otpFooterHint")}
        </p>
        {(method.id === "sms" || method.id === "whatsapp") && setPersonPhone ? (
          <div className="mt-4 grid grid-cols-3 gap-2">
            <label className="block text-xs text-gray-500 dark:text-gray-400">
              DDI
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                inputMode="numeric"
                value={settings.countryDialCode || "55"}
                onChange={(event) =>
                  setPersonPhone({
                    countryDialCode: event.target.value.replace(/\D/g, ""),
                    areaCode: settings.areaCode,
                    phoneLocalNumber: settings.phoneLocalNumber,
                  })
                }
              />
            </label>
            <label className="block text-xs text-gray-500 dark:text-gray-400">
              DDD
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                inputMode="numeric"
                value={settings.areaCode || ""}
                onChange={(event) =>
                  setPersonPhone({
                    countryDialCode: settings.countryDialCode || "55",
                    areaCode: event.target.value.replace(/\D/g, "").slice(0, 2),
                    phoneLocalNumber: settings.phoneLocalNumber,
                  })
                }
              />
            </label>
            <label className="col-span-1 block text-xs text-gray-500 dark:text-gray-400 sm:col-span-1">
              Número
              <input
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-2 text-sm dark:border-gray-700 dark:bg-gray-900"
                inputMode="numeric"
                value={settings.phoneLocalNumber || ""}
                onChange={(event) =>
                  setPersonPhone({
                    countryDialCode: settings.countryDialCode || "55",
                    areaCode: settings.areaCode,
                    phoneLocalNumber: event.target.value
                      .replace(/\D/g, "")
                      .slice(0, 9),
                  })
                }
              />
            </label>
          </div>
        ) : null}
      </div>
    </div>
  );

  const renderGoogleAuthenticatorPanel = (method) => (
    <div role="tabpanel" aria-label={method.title}>
      <MethodPanelHeader
        method={method}
        statusLabel={getMethodStatus(method.id)}
        statusClass={getStatusClass(method.id)}
      />

      {!googleAuthAllowed ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("twoFactor.googleNotAvailable")}
        </p>
      ) : null}

      {googleAuthAllowed && !googleAuthenticatorConfigured ? (
        <div className="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("twoFactor.setupGoogleTitle")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("twoFactor.setupGoogleBody")}
          </p>

          {googleSecretLoading ? (
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {intl.get("twoFactor.googleSecretLoading")}
            </p>
          ) : null}

          {googleSecretError ? (
            <p className="mt-4 text-xs text-amber-700 dark:text-amber-300">
              {googleSecretError}
            </p>
          ) : null}

          {!googleSecretLoading && hasGoogleSecret ? (
            <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row sm:items-start">
              <div className="flex h-36 w-36 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
                {googleOtpauthUrl ? (
                  <QRCode value={googleOtpauthUrl} size={120} />
                ) : null}
              </div>

              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("twoFactor.manualKeyHint")}
                </p>
                <code className="mt-1.5 inline-block break-all rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold tracking-wide text-brand-dark dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                  {googleSecret}
                </code>
              </div>
            </div>
          ) : null}

          <button
            type="button"
            className="btn-primary mt-4 w-full sm:w-auto"
            onClick={handleConfigureGoogleAuthenticator}
            disabled={isRegistering || googleSecretLoading || !hasGoogleSecret}
          >
            {intl.get("twoFactor.confirmSetup")}
          </button>
        </div>
      ) : null}

      {googleAuthAllowed && googleAuthenticatorConfigured ? (
        <div className="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
                {intl.get("twoFactor.googleConfiguredTitle")}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("twoFactor.googleConfiguredBody")}
              </p>
            </div>
            <button
              type="button"
              aria-label={intl.get("twoFactor.removeGoogleAuth")}
              onClick={handleRemoveGoogleAuthenticator}
              disabled={isRegistering}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-950/30"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const renderFidoPanel = (method) => (
    <div role="tabpanel" aria-label={method.title}>
      <MethodPanelHeader
        method={method}
        statusLabel={getMethodStatus(method.id)}
        statusClass={getStatusClass(method.id)}
      />

      {!hasFidoKey ? (
        <div className="rounded-xl border border-gray-100 p-4 dark:border-gray-800">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("twoFactor.registerUsbKeyTitle")}
          </p>
          {!webAuthnAvailable ? (
            <div className="mt-3 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5 dark:border-amber-900/50 dark:bg-amber-950/20">
              <p className="text-xs text-amber-900 dark:text-amber-200">
                {getWebAuthnUnavailableMessage()}
              </p>
            </div>
          ) : (
            <>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {intl.get("twoFactor.registerUsbKeyBody")}
              </p>

              <button
                type="button"
                className="btn-primary mt-3 w-full sm:w-auto"
                onClick={handleOpenRegisterOverlay}
                disabled={isRegistering}
              >
                {intl.get("twoFactor.startKeyRegistration")}
              </button>
            </>
          )}
        </div>
      ) : null}

      {hasFidoKey ? (
        <div>
          <p className="mb-2 text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("twoFactor.registeredKeys")}
          </p>
          <div className="mb-3 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5 dark:border-amber-900/50 dark:bg-amber-950/20">
            <p className="text-xs text-amber-900 dark:text-amber-200">
              {intl.get("twoFactor.passkeyWarning")}
            </p>
          </div>
          <ul className="space-y-2">
            {settings.credentials.map((credential) => (
              <li
                key={credential.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2.5 dark:border-gray-800"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-brand-dark dark:text-gray-100">
                    {credential.label}
                  </p>
                  <p className="text-xs text-gray-400">
                    {intl.get("twoFactor.registeredOn", {
                      date: formatDate(credential.createdAt, userLocale),
                    })}
                    {credential.transports?.includes("usb") ? ` · ${intl.get("words.usbKey")}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={intl.get("twoFactor.removeKey", { label: credential.label })}
                  onClick={() => handleRemoveKey(credential.id)}
                  disabled={isRegistering}
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-950/30"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );

  const renderMethodPanel = (method) => {
    if (!method) return null;

    if (method.id === "email" || method.id === "sms" || method.id === "whatsapp") {
      return renderOtpMethodPanel(method);
    }
    if (method.id === "google_authenticator") {
      return renderGoogleAuthenticatorPanel(method);
    }
    if (method.id === "fido") {
      return renderFidoPanel(method);
    }
    return null;
  };

  return (
    <>
    <Dialog
      className="two-factor-settings-modal"
      open={open && !registerOverlayOpen}
      onClose={handleDialogClose}
      disableEscapeKeyDown={webAuthnInProgress}
      disableEnforceFocus={webAuthnInProgress}
      disableAutoFocus={webAuthnInProgress}
      disableRestoreFocus={webAuthnInProgress}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{
        className: isMobile ? MODAL_PAPER_MOBILE : MODAL_PAPER_DESKTOP,
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:px-6">
          <h2 className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
            <FiShield className="h-5 w-5 text-[#e7a73f]" />
            {intl.get("twoFactor.title")}
          </h2>
          <button
            type="button"
            aria-label={intl.get("words.close")}
            onClick={handleClose}
            disabled={webAuthnInProgress}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          >
            <MdClose className="h-5 w-5" aria-hidden />
          </button>
        </header>

        {visibleMethods.length > 0 ? (
          <div className="shrink-0 overflow-x-auto px-4 pt-4 [-webkit-overflow-scrolling:touch] sm:px-6">
            <div
              className="stables-segment-control inline-flex w-max min-w-full"
              role="tablist"
              aria-label={intl.get("twoFactor.verificationMethods")}
            >
              {visibleMethods.map((method) => {
                const isActive = activeMethod?.id === method.id;

                return (
                  <button
                    key={method.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveMethodId(method.id)}
                    className={`shrink-0 whitespace-nowrap rounded-md px-3 py-2 text-center text-xs font-semibold transition sm:px-4 sm:py-1.5 ${
                      isActive
                        ? "bg-[#e7a73f] text-white shadow-sm dark:bg-[#e7a73f] dark:text-white"
                        : "text-gray-500 hover:text-brand-dark dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                  >
                    {method.segmentLabel}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {renderMethodPanel(activeMethod)}
        </div>

        <footer className="mt-auto shrink-0 border-t border-gray-100 bg-white px-4 pb-5 pt-4 dark:border-gray-800 dark:bg-brand-dark-surface sm:px-6">
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("twoFactor.primaryMethod")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("twoFactor.primaryMethodHint")}
          </p>

          <RadioGroup
            className="mt-3"
            value={preferredLogin}
            onChange={(event) => handlePreferredLoginChange(event.target.value)}
          >
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {LOGIN_METHOD_OPTIONS.filter((option) =>
                isMethodAllowedByPolicy(option.id, settings.policy.allowed)
              ).map((option) => {
                const isDisabled =
                  (option.id === "fido" && (!hasFidoKey || !webAuthnAvailable)) ||
                  (option.id === "google_authenticator" &&
                    !googleAuthenticatorConfigured);

                return (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    className="!mr-0"
                    control={<Radio size="small" color="primary" disabled={isDisabled} />}
                    label={
                      <span className="text-sm text-brand-dark dark:text-gray-100">
                        {intl.get(option.labelKey)}
                        {option.id === "fido" && !hasFidoKey ? (
                          <span className="ml-1 text-xs text-gray-400">
                            {webAuthnAvailable
                              ? intl.get("twoFactor.registerAbove")
                              : intl.get("twoFactor.unavailableBrowser")}
                          </span>
                        ) : null}
                        {option.id === "google_authenticator" &&
                        !googleAuthenticatorConfigured ? (
                          <span className="ml-1 text-xs text-gray-400">
                            {intl.get("twoFactor.configureAbove")}
                          </span>
                        ) : null}
                      </span>
                    }
                  />
                );
              })}
            </div>
          </RadioGroup>

          <p className="mt-4 text-sm font-semibold text-brand-dark dark:text-gray-100">
            {intl.get("twoFactor.backupMethods")}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {intl.get("twoFactor.backupMethodsHint", {
              max: Math.max(0, MAX_SELECTED_LOGIN_METHODS - 1),
            })}
          </p>

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            {LOGIN_METHOD_OPTIONS.filter((option) =>
              isMethodAllowedByPolicy(option.id, settings.policy.allowed)
            ).map((option) => {
              const isPrincipal = option.id === preferredLogin;
              const isChecked = selectedLoginMethods.includes(option.id);
              const isDisabled =
                isPrincipal ||
                (!isChecked && atLoginMethodLimit) ||
                (option.id === "fido" && (!hasFidoKey || !webAuthnAvailable)) ||
                (option.id === "google_authenticator" &&
                  !googleAuthenticatorConfigured);

              return (
                <FormControlLabel
                  key={`backup-${option.id}`}
                  className="!mr-0"
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={() => handleBackupMethodToggle(option.id)}
                    />
                  }
                  label={
                    <span className="text-sm text-brand-dark dark:text-gray-100">
                      {intl.get(option.labelKey)}
                      {isPrincipal ? (
                        <span className="ml-1 text-xs text-gray-400">
                          {intl.get("twoFactor.primaryBadge")}
                        </span>
                      ) : null}
                    </span>
                  }
                />
              );
            })}
          </div>

          {atLoginMethodLimit ? (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              {intl.get("twoFactor.maxMethodsSelected", { max: MAX_SELECTED_LOGIN_METHODS })}
            </p>
          ) : null}

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              className="btn-outline w-full sm:w-auto"
              onClick={handleClose}
              disabled={webAuthnInProgress}
            >
              {intl.get("words.close")}
            </button>
          </div>
        </footer>
      </div>
    </Dialog>

    {registerOverlayOpen &&
      createPortal(
        <div
          className="fido-register-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fido-register-overlay-title"
        >
          <div className="fido-register-overlay__card app-card">
            <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:px-6">
              <h2
                id="fido-register-overlay-title"
                className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                <FiKey className="h-5 w-5 text-[#e7a73f]" />
                {intl.get("twoFactor.registerOverlayTitle")}
              </h2>
              <button
                type="button"
                aria-label={intl.get("words.close")}
                onClick={handleCloseRegisterOverlay}
                disabled={webAuthnInProgress}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-gray-100"
              >
                <MdClose className="h-5 w-5" aria-hidden />
              </button>
            </header>

            <div className="px-4 py-5 sm:px-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("twoFactor.registerOverlayBody")}
              </p>

              <label className="mt-4 block">
                <span className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                  {intl.get("twoFactor.keyNameLabel")}
                </span>
                <input
                  type="text"
                  className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  value={keyLabel}
                  onChange={(event) => setKeyLabel(event.target.value)}
                  placeholder={intl.get("twoFactor.keyNamePlaceholder")}
                  disabled={webAuthnInProgress}
                />
              </label>

              <div className="mt-5">
                <FidoTouchPanel
                  key="fido-register-panel"
                  onWebAuthn={handleRegisterWebAuthn}
                  onSuccess={handleRegisterSuccess}
                  onError={handleRegisterError}
                  onWebAuthnStateChange={setWebAuthnInProgress}
                  successMessage={intl.get("twoFactor.keyRegisteredShort")}
                  idleMessage={intl.get("twoFactor.registerIdleMessage")}
                  buttonLabel={intl.get("twoFactor.registerWithUsbKey")}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
